<template>
  <div>
    <v-tabs class="elevation-1 mb-1" v-model="activeTab" v-bind="attributes.vTabs">
      <v-tab
        v-for="(item, index) in storeHeaders"
        :key="index"
        :name="`tab-${item.id}`"
        :id="`tab-${item.id}`"
        v-bind="item.attributes"
        v-show="item.show"
        :disabled="item.readonly"
        @click="reloadTab(index)"
        @contextmenu="selectComponentDEV($event, tabsSchema.model, index)"
        :class="[getSelectedDEV & (getSelectedTabDEV === index) && 'dev-selected-component__no-change-padding']"
      >
        {{ item.title }}
        <v-icon>{{ item.icon }}</v-icon>
      </v-tab>

      <v-tabs-items v-bind="attributes.vTabItems" v-model="activeTab">
        <v-tab-item v-for="(item, index) in content" :disabled="item.disabled" :key="index">
          <v-card :text="item.cardtabFlat" :color="item.cardBackgroundColor">
            <v-form v-model="valid" ref="form_tab">
              <template v-if="item.groups.length">
                <i4proColumnGroup
                  v-for="group in item.groups"
                  :schema="group"
                  :key="group.id"
                  :mode="'form'"
                  :page="page"
                  :tab="activeTab + 1"
                />
              </template>

              <i4proColumnFields
                class="flex-item"
                v-if="item.fields.length"
                :page="page"
                :mode="'form'"
                :schema="item.fields"
                :tab="activeTab + 1"
              />
            </v-form>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-tabs>

    <component
      v-if="renderChild"
      :is="childComponent"
      :page="childPage"
      :context="childContext"
      :origin="'page'"
    />
  </div>
</template>

<script>
import { COMPONENT_TYPE } from '@/enums/types';
import * as HANDLERS from '@/helpers/types/handlers';

import devComponentMixin from '@/mixins/dev-component-mixin';

export default {
  mixins: [devComponentMixin],

  components: {
    i4proColumnGroup: () => import('@/components/partials/i4pro-column-group'),
    i4proColumnFields: () => import('@/components/partials/i4pro-column-fields'),
    i4proForm: () => import('@/components/layouts/i4pro-form'),
    i4proList: () => import('@/components/layouts/i4pro-list')
  },

  props: {
    page: { type: String, required: true },
    attributes: { type: Object, default: () => ({}) },
    mode: { type: String, default: '' },
    content: { type: Object, required: true },
    tabsSchema: { type: Object, default: () => ({}) }
  },

  data() {
    return {
      activeTab: null,
      valid: false,
      renderChild: false,
      childComponent: '',
      childPage: 0,
      childContext: '',
      initialEvent: true,
      schema: this.tabsSchema
    };
  },

  computed: {
    storeHeaders() {
      return this.$store.getters['page/schema']({
        page: this.page,
        mode: this.mode
      }).tabs.headers;
    },

    masterModel() {
      return this.$store.getters['page/model']({
        page: this.page,
        mode: 'master'
      });
    }
  },

  watch: {
    activeTab(newValue, oldValue) {
      this.loadTab(newValue);

      if (oldValue !== null && newValue !== oldValue) this.$emit(HANDLERS.RESET_BELOW, this.page);

      this.$emit('active-tab', newValue + 1);
    }
  },

  events: {
    [HANDLERS.VALIDATE_TAB]: 'validate',
    [HANDLERS.CHANGE_TAB]: 'changeTab',
    [HANDLERS.CHANGE_ACTIVE_TAB]: 'changeActiveTab',
    [HANDLERS.RELOAD_TAB]: 'reloadTab',
    [HANDLERS.RESET_TAB]: 'reset',
    [HANDLERS.CHANGE_COMPONENT_IN_TAB]: 'changeChildComponent',
    [HANDLERS.SHOW_GROUPS]: 'showGroups',
    [HANDLERS.HIDE_GROUP]: 'hideGroup',
    [HANDLERS.ENABLE_CHILD]: 'enableChild'
  },

  methods: {
    showGroups({ page, mode, tab }) {
      if (!tab) return;

      if (this.page !== page || this.mode !== mode) return;

      for (let group in this.content[tab].groups) this.content[tab].groups[group].visible = true;
    },

    enableChild(page) {
      if (this.page !== page) return;
      this.renderChild = true;
    },

    hideGroup({ page, mode, tab, group }) {
      if (!tab) return;

      if (this.page !== page || this.mode !== mode) return;

      this.content[tab].groups[group].visible = false;
    },

    validate(page) {
      if (this.page !== page) return;

      this.valid = this.$refs.form_tab[this.activeTab].validate();

      this.$emit(HANDLERS.RESPONSE_TAB_VALIDATE, this.valid);
    },

    changeChildComponent({ page, context }) {
      if (this.childPage === page) {
        this.childComponent = COMPONENT_TYPE[context == 'list' ? 'list' : 'form'];
        this.childContext = context;
      }
    },

    async reset(page) {
      if (page !== this.page) return;

      let tab = this.activeTab;

      if (this.masterModel['default_tab']) {
        tab = this.masterModel['default_tab'] - 1;
        this.activeTab = tab;
      }

      await this.loadTab(tab);
    },

    async reloadTab(index) {
      const tab = index !== undefined ? index : this.activeTab;

      if (tab === this.activeTab) this.loadTab(tab);
    },

    async loadTab(index, mode) {
      let current = index + 1;

      if (current === 1 && this.initialEvent && this.masterModel) {
        if ('default_tab' in this.masterModel && this.masterModel['default_tab'] !== current) {
          current = this.masterModel['default_tab'];
          this.activeTab = this.masterModel['default_tab'] - 1;
        }
      }

      this.renderChild = false;

      await this.$store.dispatch('page/setTabScheme', { page: this.page, tab: current, mode: this.mode });

      if (this.page === 'dev') {
        // --> Altera activeTab para controle interno do modulo DEV!
        this.$store.dispatch('dev/setActiveTab', current);

        // --> Intercepta o carregamento de páginas no modulo DEV!
        if ('page' in this.content[current]) return;
      }

      if ('page' in this.content[current]) {
        if (mode && this.content[current].page.mode === 'list' && mode !== 'list') {
          await this.$store.dispatch('page/getEntry', { page: this.content[current].page.page, mode, changeMode: false });
        } else {
          await this.$store.dispatch('page/load', {
            page: this.content[current].page.page,
            mode: this.content[current].page.mode,
            parent: this.page,
            setAutomatically: this.content[current].page.setAutomatically
          });

          this.childComponent = COMPONENT_TYPE[this.content[current].page.mode == 'list' ? 'list' : 'form'];
          this.childPage = this.content[current].page.page;
          this.childContext = this.content[current].page.mode;
          this.renderChild = true;
        }
      } else {
        this.renderChild = false;
        await this.$store.dispatch('page/loadTab', { page: this.page, tab: current, mode: this.mode });
        await this.$store.dispatch('page/loadLookups', { page: this.page, tab: current, mode: this.mode });
      }

      this.initialEvent = false;
      this.$store.dispatch('page/conditions', { page: this.page, mode: this.mode, tab: current });
    },

    async changeTab({ page, tab, mode, parentPage }) {
      const currentPage = parentPage || page;

      if (this.page !== currentPage) return;

      let newTab = tab - 1;

      if (newTab === this.activeTab) {
        this.loadTab(newTab, mode);
        return;
      }

      this.activeTab = newTab;
    },

    changeActiveTab(tab) {
      this.activeTab = tab;
    }
  }
};
</script>

<style>
.flex-item {
  order: -1;
}
</style>
